import { Menu, Search } from 'lucide-react'
import { type FormEvent, type ReactElement, useState } from 'react'
import { toast } from 'sonner'

import { ThemeToggle } from '@/components/theme/theme-toggle'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'

import { UserMenu } from './user-menu'

export interface TopbarProps {
  /** Abre a gaveta de navegação. Só aparece abaixo de `md`. */
  readonly aoAbrirNavegacao: () => void
}

/**
 * Barra superior: busca, tema e menu do usuário.
 *
 * A busca ainda não tem índice para consultar — em vez de fingir que buscou,
 * ela avisa em qual fase o recurso chega. Nada aqui simula resultado.
 */
export function Topbar({ aoAbrirNavegacao }: TopbarProps): ReactElement {
  const [termo, setTermo] = useState('')

  function aoSubmeter(evento: FormEvent<HTMLFormElement>): void {
    evento.preventDefault()

    if (termo.trim().length === 0) {
      return
    }

    toast.info('Busca global ainda não disponível', {
      description: 'Ela é ligada junto com os módulos de frota e reservas.',
    })
  }

  return (
    <header
      className={cn(
        'topbar-surface sticky top-0 z-30 flex h-14 shrink-0 items-center gap-2',
        'border-b border-border px-3 backdrop-blur sm:px-4',
      )}
    >
      <Button
        variant="ghost"
        size="icon"
        className="md:hidden"
        onClick={aoAbrirNavegacao}
        aria-label="Abrir navegação"
      >
        <Menu className="size-5" aria-hidden="true" />
      </Button>

      <form role="search" className="flex min-w-0 flex-1 items-center" onSubmit={aoSubmeter}>
        <div className="relative w-full max-w-md">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fg-muted"
            aria-hidden="true"
          />
          <Input
            type="search"
            value={termo}
            onChange={(evento) => {
              setTermo(evento.target.value)
            }}
            placeholder="Buscar reserva, cliente ou placa…"
            aria-label="Buscar no painel"
            className="h-9 pl-9"
          />
        </div>
      </form>

      <div className="flex shrink-0 items-center gap-1">
        <Separator orientation="vertical" className="mx-1 hidden h-6 sm:block" />
        <ThemeToggle />
        <UserMenu />
      </div>
    </header>
  )
}
